import React, { useState, useMemo } from "react";
import { toast } from "react-toastify";
import { emitLeadUpdate } from "../utils/leadSync";
import API from "../utils/api";
import {
  LEAD_FIELD_QUICK_STATUS_VALUES,
  getLeadStatusColor,
} from "../constants/leadStatuses";

import {
  Drawer,
  Box,
  Typography,
  Button,
  Divider,
  useMediaQuery,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  FormHelperText,
} from "@mui/material";

export default function LeadActionPanel({ open, onClose, lead, onNext }) {
  const isMobile = useMediaQuery("(max-width:768px)");
  const [status, setStatus] = useState(null);
  const [followUpAt, setFollowUpAt] = useState("");

  const safeLead = lead || {
    first_name: "",
    last_name: "",
    name: "",
    address: "",
    latitude: 0,
    longitude: 0,
    notes: "",
  };

  const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${safeLead.latitude},${safeLead.longitude}`;
  const needsFollowUpDate = status === "Follow Up Needed";
  const scheduleDisabled = needsFollowUpDate && !followUpAt;

  const instantStatuses = [
    "Not Home",
    "Not Interested",
    "Vacant Property",
    "Under Construction",
    "Left Flyer",
    "Renter",
  ];

  const handleStatusChange = async (_e, value) => {
    if (!value) return;
    setStatus(value);

    if (value !== "Follow Up Needed") setFollowUpAt("");

    const leadId = lead.lead_id || lead.id;

    if (instantStatuses.includes(value) && leadId && !isNaN(Number(leadId))) {
      try {
        await API.patch(`leads/${leadId}/`, { status: value });
        emitLeadUpdate({ id: Number(leadId), status: value });

        const newColor = getLeadStatusColor(value);
        document.querySelectorAll(".modern-pin").forEach((el) => {
          const markerParent = el.closest("[data-lead-id]");
          if (markerParent && markerParent.dataset.leadId === String(lead.id)) {
            el.style.background = newColor;
          }
        });

        toast.success(`Status updated to ${value}`);
        onClose?.();
        setTimeout(() => onNext?.(), 300);
      } catch (err) {
        console.error("Status update failed:", err);
        toast.error(`Failed to update status (${err?.response?.status}).`);
      }
    } else if (instantStatuses.includes(value)) {
      toast.error("Missing backend Lead ID — cannot update this stop.");
    }
  };

  const followupMin = useMemo(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  }, []);

  if (!lead) return null;

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : 320,
          height: isMobile ? "22vh" : "50vh",
          p: 2,
          borderRadius: isMobile ? "16px 16px 0 0" : 0,
          boxShadow: 5,
          position: "absolute",
          top: isMobile ? "auto" : 80,
          bottom: isMobile ? "2%" : "auto",
          transform: isMobile ? "translateY(-20%)" : "none",
          backgroundColor: "background.paper",
          overflow: "hidden",
        },
      }}
      ModalProps={{
        keepMounted: true,
        container: document.body,
      }}
    >
      <Box sx={{ height: "100%", overflowY: "auto" }}>
        <Typography variant="h6" fontWeight={700} color="primary.main">
          {safeLead.name ||
            `${safeLead.first_name || ""} ${safeLead.last_name || ""}`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {safeLead.address}
        </Typography>

        <Divider sx={{ my: 1 }} />

        <Typography variant="body2" sx={{ mb: 1 }}>
          {safeLead.notes || "No notes available."}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            href={googleMapsUrl}
            target="_blank"
          >
            Get Directions
          </Button>
        </Box>
        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
          Update Status:
        </Typography>
        <ToggleButtonGroup
          value={status}
          exclusive
          onChange={handleStatusChange}
          size="small"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.75,
            "& .MuiToggleButton-root": { px: 1, py: 0.5 },
          }}
        >
          {LEAD_FIELD_QUICK_STATUS_VALUES.map((opt) => (
            <ToggleButton key={opt} value={opt}>
              {opt}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {needsFollowUpDate && (
          <Box sx={{ mt: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              label="Follow-up Date & Time"
              type="datetime-local"
              value={followUpAt}
              onChange={(e) => setFollowUpAt(e.target.value)}
              inputProps={{ min: followupMin }}
            />
            <FormHelperText>
              A date & time is required when status is “Follow Up Needed”.
            </FormHelperText>
          </Box>
        )}

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            disabled={scheduleDisabled}
            onClick={() =>
              alert(
                needsFollowUpDate
                  ? `Schedule follow-up at ${followUpAt}`
                  : "Select status 'Follow Up Needed' to schedule"
              )
            }
          >
            Schedule Follow-up
          </Button>

          {onNext && (
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={onNext}
              sx={{ ml: "auto" }}
            >
              Next →
            </Button>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
